import React, { useState } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiChevronLeft, FiChevronRight, FiHeart, FiBriefcase, FiActivity, FiDollarSign } from 'react-icons/fi';
import SEO from '../components/common/SEO';
import { useHydrated } from '../hooks/useHydrated';
import { zodiacSigns, getZodiacByName } from '../data/zodiacSigns';
import { dailyHoroscopes } from '../data/mockData';
import { useTranslation } from '../hooks/useTranslation';
import { horoscopeCategoryTamil } from '../data/horoscopeCategoryTamil';
import { horoscopePredictionTamil } from '../data/horoscopePredictionTamil';
import './HoroscopeDetail.css';

const elementTamil = { Fire: 'நெருப்பு', Earth: 'பூமி', Air: 'காற்று', Water: 'நீர்' };
const qualityTamil = { Cardinal: 'சர ராசி', Fixed: 'ஸ்திர ராசி', Mutable: 'உபய ராசி' };
const planetTamil = { Mars: 'செவ்வாய்', Venus: 'சுக்கிரன்', Mercury: 'புதன்', Moon: 'சந்திரன்', Sun: 'சூரியன்', Jupiter: 'குரு', Saturn: 'சனி', Pluto: 'புளூட்டோ', Uranus: 'யுரேனஸ்', Neptune: 'நெப்டியூன்' };
const dayTamil = { Sunday: 'ஞாயிறு', Monday: 'திங்கள்', Tuesday: 'செவ்வாய்', Wednesday: 'புதன்', Thursday: 'வியாழன்', Friday: 'வெள்ளி', Saturday: 'சனி' };
const signNameTamil = { Aries: 'மேஷம்', Taurus: 'ரிஷபம்', Gemini: 'மிதுனம்', Cancer: 'கடகம்', Leo: 'சிம்மம்', Virgo: 'கன்னி', Libra: 'துலாம்', Scorpio: 'விருச்சிகம்', Sagittarius: 'தனுசு', Capricorn: 'மகரம்', Aquarius: 'கும்பம்', Pisces: 'மீனம்' };

const HoroscopeDetail = () => {
  const { sign } = useParams();
  const location = useLocation();
  const [period, setPeriod] = useState('daily');
  const hydrated = useHydrated();
  const { t, lang } = useTranslation();
  const ta = lang === 'ta';

  const signData = getZodiacByName(sign);
  const currentIndex = signData ? zodiacSigns.findIndex(s => s.id === signData.id) : -1;
  const prevSign = zodiacSigns[(currentIndex - 1 + 12) % 12];
  const nextSign = zodiacSigns[(currentIndex + 1) % 12];

  const signKey = signData ? signData.name.toLowerCase() : sign.toLowerCase();
  const horoscopeData = dailyHoroscopes[signKey] || dailyHoroscopes.aries;

  if (!signData) {
    return (
      <div className="horoscope-detail-page">
        <div className="container">
          <h1>{ta ? 'ராசி கிடைக்கவில்லை' : 'Sign not found'}</h1>
          <Link to="/horoscope" className="btn btn-primary">{ta ? 'ராசிபலன்களுக்குத் திரும்பு' : 'Back to Horoscopes'}</Link>
        </div>
      </div>
    );
  }

  const isZodiacRoute = location.pathname.startsWith('/zodiac/');
  const canonicalBase = isZodiacRoute ? '/zodiac' : '/horoscope';

  return (
    <div className="horoscope-detail-page">
      <SEO
        title={`${signData.name} (${signData.tamilNameEn}) Horoscope Today - Daily ${signData.name} Predictions`}
        description={`Today's ${signData.name} (${signData.tamilNameEn}) horoscope (${signData.dates}). Get daily, weekly, and monthly predictions. ${signData.element} sign ruled by ${signData.ruling_planet}. Lucky numbers: ${signData.lucky_numbers.join(', ')}.`}
        keywords={`${signData.name.toLowerCase()} horoscope, ${signData.tamilSlug} rasi, ${signData.tamilNameEn}, ${signData.name.toLowerCase()} horoscope today, ${signData.name.toLowerCase()} daily prediction`}
        canonicalPath={`${canonicalBase}/${sign}`}
        ogType="article"
        jsonLd={[{
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": `${signData.name} Horoscope Today`,
          "description": `Daily horoscope for ${signData.name} (${signData.dates})`,
          "author": { "@type": "Organization", "name": "AstroEpanchang" },
          "publisher": { "@type": "Organization", "name": "AstroEpanchang" }
        },{"@context":"https://schema.org","@type":"FAQPage","mainEntity":[{"@type":"Question","name":"Should I follow my Sun sign or Moon sign horoscope?","acceptedAnswer":{"@type":"Answer","text":"In Vedic astrology, the Moon sign horoscope is considered more accurate for daily predictions as it reflects your mind and emotional state. The Sun sign relates to your core identity. For the most comprehensive reading, consider both your Moon sign and Ascendant (Lagna) sign horoscopes."}},{"@type":"Question","name":"How are daily horoscope predictions calculated?","acceptedAnswer":{"@type":"Answer","text":"Daily horoscopes are based on the transit of planets through the zodiac relative to your birth Moon sign. The Moon's daily transit through different Nakshatras creates varying emotional and practical influences for each sign. Major transits of Saturn, Jupiter, and Rahu-Ketu provide the backdrop for longer-term predictions."}},{"@type":"Question","name":"What makes Vedic horoscopes different from Western horoscopes?","acceptedAnswer":{"@type":"Answer","text":"Vedic horoscopes use the sidereal zodiac (based on actual star positions) while Western horoscopes use the tropical zodiac (based on seasons). Vedic astrology also incorporates Nakshatras (27 lunar mansions), Dasha systems, and divisional charts for more detailed and personalized predictions."}},{"@type":"Question","name":"How can I get more accurate horoscope predictions?","acceptedAnswer":{"@type":"Answer","text":"For the most accurate predictions, know your exact birth time and generate your Kundli (birth chart). Read predictions for your Moon sign rather than just your Sun sign. Consider the current Dasha period in your chart, as this provides the most personalized context for understanding how planetary transits affect you specifically."}}]}]}
      />
      <div className="container">
        {/* Navigation between signs */}
        <div className="sign-navigation">
          <Link to={isZodiacRoute ? `/zodiac/${prevSign.name.toLowerCase()}-${prevSign.tamilSlug}-rasi` : `/horoscope/${prevSign.name.toLowerCase()}`} className="nav-sign prev">
            <FiChevronLeft />
            <span>{ta ? `${prevSign.tamilName} (${prevSign.name})` : `${prevSign.name} (${prevSign.tamilNameEn})`}</span>
          </Link>
          <Link to={isZodiacRoute ? `/zodiac/${nextSign.name.toLowerCase()}-${nextSign.tamilSlug}-rasi` : `/horoscope/${nextSign.name.toLowerCase()}`} className="nav-sign next">
            <span>{ta ? `${nextSign.tamilName} (${nextSign.name})` : `${nextSign.name} (${nextSign.tamilNameEn})`}</span>
            <FiChevronRight />
          </Link>
        </div>

        {/* Sign Header */}
        <motion.div
          className="sign-header"
          style={{ '--sign-color': signData.color }}
          initial={hydrated ? { opacity: 0, y: -20 } : false}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="sign-symbol-large">{signData.symbol}</div>
          <div className="sign-info">
            <h1>{ta ? signData.tamilName : signData.name} <span style={{ fontSize: '0.6em', color: '#ff9800' }}>({ta ? signData.name : signData.tamilNameEn})</span></h1>
            <p className="sign-dates">{signData.dates}</p>
            <div className="sign-meta">
              <span className="meta-item">
                <strong>{t('horoscopeDetail.element')}:</strong> {ta ? elementTamil[signData.element] : signData.element}
              </span>
              <span className="meta-item">
                <strong>{t('horoscopeDetail.rulingPlanet')}:</strong> {ta ? planetTamil[signData.ruling_planet] : signData.ruling_planet}
              </span>
              <span className="meta-item">
                <strong>{t('horoscopeDetail.quality')}:</strong> {ta ? qualityTamil[signData.quality] : signData.quality}
              </span>
            </div>
          </div>
        </motion.div>

        {/* Period Tabs */}
        <div className="period-tabs">
          {['daily', 'weekly', 'monthly'].map((p) => (
            <button
              key={p}
              className={`period-tab ${period === p ? 'active' : ''}`}
              onClick={() => setPeriod(p)}
            >
              {t(`horoscopeDetail.${p}`)}
            </button>
          ))}
        </div>

        {/* Horoscope Content */}
        <motion.div
          className="horoscope-content"
          key={period}
          initial={hydrated ? { opacity: 0, y: 20 } : false}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="main-prediction">
            <h3>{period === 'daily' ? t('horoscopeDetail.yourDailyHoroscope') : period === 'weekly' ? t('horoscopeDetail.yourWeeklyHoroscope') : t('horoscopeDetail.yourMonthlyHoroscope')}</h3>
            <p>{ta && horoscopePredictionTamil[signKey] ? horoscopePredictionTamil[signKey][period] : horoscopeData[period]}</p>
          </div>

          {/* Category Predictions */}
          <div className="category-predictions">
            {[
              { key: 'love', icon: <FiHeart />, cls: 'love' },
              { key: 'career', icon: <FiBriefcase />, cls: 'career' },
              { key: 'health', icon: <FiActivity />, cls: 'health' },
              { key: 'finance', icon: <FiDollarSign />, cls: 'finance' },
            ].map(({ key, icon, cls }) => {
              const catData = horoscopeData[key] || { text: '', rating: 3 };
              const tamilData = horoscopeCategoryTamil[signKey];
              return (
                <div className="category-card" key={key}>
                  <div className={`category-icon ${cls}`}>{icon}</div>
                  <h4>{t(`horoscopeDetail.${key}`)}</h4>
                  <p>{ta && tamilData ? tamilData[key] : catData.text}</p>
                  <div className="rating">
                    {'★'.repeat(catData.rating)}{'☆'.repeat(5 - catData.rating)}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Lucky Section */}
          <div className="lucky-section">
            <h3>{ta ? 'இன்றைய அதிர்ஷ்ட அம்சங்கள்' : "Today's Lucky Elements"}</h3>
            <div className="lucky-grid">
              <div className="lucky-item">
                <span className="lucky-label">{t('horoscopeDetail.luckyNumbers')}</span>
                <span className="lucky-value">{signData.lucky_numbers.join(', ')}</span>
              </div>
              <div className="lucky-item">
                <span className="lucky-label">{t('horoscopeDetail.luckyDay')}</span>
                <span className="lucky-value">{ta ? dayTamil[signData.lucky_day] : signData.lucky_day}</span>
              </div>
              <div className="lucky-item">
                <span className="lucky-label">{ta ? 'அதிர்ஷ்ட நிறம்' : 'Lucky Color'}</span>
                <span className="lucky-value" style={{ color: signData.color }}>●</span>
              </div>
              <div className="lucky-item">
                <span className="lucky-label">{t('horoscopeDetail.compatibility')}</span>
                <span className="lucky-value">{ta ? signData.compatibility.slice(0, 2).map(s => signNameTamil[s] || s).join(', ') : signData.compatibility.slice(0, 2).join(', ')}</span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* CTA Section */}
        <div className="detail-cta">
          <h3>{ta ? 'மேலும் தனிப்பயனாக்கப்பட்ட நுண்ணறிவுகள் வேண்டுமா?' : 'Want More Personalized Insights?'}</h3>
          <p>{ta ? 'உங்கள் முழு பிறப்பு விளக்கப்படம் மற்றும் விரிவான ஜாதக பகுப்பாய்வை ஆராயுங்கள்' : 'Explore your full birth chart and detailed horoscope analysis'}</p>
          <Link to="/online-horoscope-generator" className="btn btn-primary">{ta ? 'இலவச ஜாதகம் பெறுங்கள்' : 'Get Free Horoscope'}</Link>
        </div>

        {/* Bottom SEO Content - Dynamic per sign */}
        <div className="page-bottom-content">
          <h2>{signData.name} Zodiac Sign - Complete Guide to {signData.name} Personality, Traits & Horoscope</h2>
          <p>{signData.description} Born between {signData.dates}, {signData.name} is a {signData.quality} {signData.element} sign ruled by {signData.ruling_planet}. Understanding your {signData.name} zodiac sign helps you unlock your unique strengths, navigate challenges, and align with your cosmic purpose.</p>

          <h3>{signData.name} Personality Traits & Characteristics</h3>
          <p>{signData.name} individuals are known for their distinctive qualities: {signData.traits.join(', ')}. As a {signData.quality} sign, {signData.name} {signData.quality === 'Cardinal' ? 'initiates action and leads others — a natural pioneer who thrives on new beginnings and creating change' : signData.quality === 'Fixed' ? 'demonstrates remarkable willpower and perseverance — steady, reliable, and determined to see things through to completion' : 'adapts easily to changing circumstances — versatile, flexible, and skilled at communicating and synthesizing different ideas'}. The {signData.element} element infuses {signData.name} with {signData.element === 'Fire' ? 'passion, inspiration, and boundless enthusiasm — an unstoppable drive to pursue goals and inspire others' : signData.element === 'Earth' ? 'practicality, groundedness, and a deep appreciation for tangible results — building lasting foundations in all areas of life' : signData.element === 'Air' ? 'intellectual curiosity, social grace, and a gift for communication — connecting ideas and people with effortless ease' : 'emotional depth, intuition, and empathic sensitivity — an uncanny ability to feel and understand the hidden currents of life'}.</p>

          <h3>{signData.name} Love & Relationship Compatibility</h3>
          <p>In love and relationships, {signData.name} is most naturally compatible with {signData.compatibility.slice(0, 2).join(' and ')}, sharing harmonious elemental energy and complementary life values. {signData.compatibility[2]} and {signData.compatibility[3] || signData.compatibility[0]} also form strong bonds with {signData.name}, bringing balance and growth through their differences. In Vedic astrology, relationship compatibility goes beyond just Sun signs — the Nakshatra of both partners, their Moon signs, and the 7th house of the birth chart all play crucial roles in determining true compatibility. Use our free Kundli matching tool for a comprehensive compatibility analysis.</p>

          <h3>{signData.name} Career, Finance & Life Path</h3>
          <p>Ruled by {signData.ruling_planet}, {signData.name} natives excel in careers that align with their {signData.element} energy and {signData.quality} initiative. The influence of {signData.ruling_planet} shapes their professional approach, ambitions, and financial patterns. {signData.name} individuals tend to thrive in environments where their natural traits — {signData.traits.slice(0, 3).join(', ')} — are valued and utilized. Lucky days for {signData.name} include {signData.lucky_day}, and lucky numbers {signData.lucky_numbers.join(', ')} can be used when making important decisions or beginning new ventures.</p>

          <h3>Frequently Asked Questions About {signData.name}</h3>
          <div className="faq-seo">
            <div className="faq-seo-item">
              <h4>What are the key strengths of {signData.name}?</h4>
              <p>{signData.name}'s greatest strengths are {signData.traits.join(', ')}. These qualities, combined with the influence of ruling planet {signData.ruling_planet} and the {signData.element} element, make {signData.name} individuals natural leaders in their chosen fields. Their {signData.quality} quality ensures they bring a unique energy to every situation — whether initiating, sustaining, or adapting to life's challenges.</p>
            </div>
            <div className="faq-seo-item">
              <h4>Which signs are most compatible with {signData.name}?</h4>
              <p>{signData.name} has the strongest compatibility with {signData.compatibility.join(', ')}. In Vedic astrology, compatibility is analyzed through Nakshatra matching (12 Porutham or Ashtakoota systems) rather than just zodiac signs. For accurate marriage compatibility, use our free horoscope matching tool which analyzes both partners' birth Nakshatras, Moon signs, and planetary placements.</p>
            </div>
            <div className="faq-seo-item">
              <h4>What is {signData.name}'s ruling planet and its significance?</h4>
              <p>{signData.ruling_planet} rules {signData.name}, and this planet's placement in your birth chart significantly influences your personality, career, relationships, and life events. A strong {signData.ruling_planet} in your Kundli amplifies {signData.name}'s positive traits and provides resilience through challenges. The current transit of {signData.ruling_planet} through the zodiac directly affects {signData.name}'s daily horoscope and major life predictions.</p>
            </div>
            <div className="faq-seo-item">
              <h4>How does Vedic astrology describe {signData.name}?</h4>
              <p>In Vedic astrology, {signData.name} (known as {signData.name === 'Aries' ? 'Mesha' : signData.name === 'Taurus' ? 'Vrishabha' : signData.name === 'Gemini' ? 'Mithuna' : signData.name === 'Cancer' ? 'Kataka' : signData.name === 'Leo' ? 'Simha' : signData.name === 'Virgo' ? 'Kanya' : signData.name === 'Libra' ? 'Tula' : signData.name === 'Scorpio' ? 'Vrischika' : signData.name === 'Sagittarius' ? 'Dhanu' : signData.name === 'Capricorn' ? 'Makara' : signData.name === 'Aquarius' ? 'Kumbha' : 'Meena'} Rashi) is a {signData.quality} {signData.element} sign. The Nakshatras that fall within {signData.name} have their own unique characteristics that refine and differentiate individuals born under this sign. Your Moon sign horoscope in {signData.name} is considered your primary Vedic horoscope, offering the most accurate daily predictions based on the Moon's current transit relative to your birth Nakshatra.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HoroscopeDetail;
